"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ArrowUpRight } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { Plus_Jakarta_Sans } from "next/font/google"

import { useGetEcosystemGroupsQuery } from "@/services/ecoType/queries"
import { apiGetAllNews } from "@/services/newsHome/api"
import { useQueries } from "@tanstack/react-query"

// Cấu hình font
const jakarta = Plus_Jakarta_Sans({
    subsets: ['vietnamese'],
    weight: ['400', '500', '600', '700', '800'],
    display: 'swap'
})

// Helper function
function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

// Dữ liệu dự phòng (Đã cập nhật link theo chuẩn mới)
const FALLBACK_CATEGORIES = [
    {
        id: "fallback-1",
        index: "01",
        title: "LĨNH VỰC KINH DOANH",
        subTitle: "DU LỊCH & KHÁCH SẠN",
        description: "Chuyên cung cấp tour du lịch trong nước và quốc tế chất lượng cao, Teambuilding & MICE chuyên nghiệp, nổi bật với ứng dụng Rovi Travel.",
        image: "/images/InfoHST/DL-KS.png",
        link: "/ecosystem/rovi-travel",
        latestNews: [],
    },
    {
        id: "fallback-2",
        index: "02",
        title: "LĨNH VỰC KINH DOANH",
        subTitle: "HOSPITALITY",
        description: "Cung cấp giải pháp định vị, giám sát hành trình và dịch vụ logistics toàn diện.",
        image: "/images/InfoHST/NH.png",
        link: "/ecosystem/rovi-hospitality",
        latestNews: [],
    },
    {
        id: "fallback-3",
        index: "03",
        title: "LĨNH VỰC KINH DOANH",
        subTitle: "LOGISTIC",
        description: "Phát triển nền tảng công nghệ du lịch với app Booking thông minh và Agent AI.",
        image: "/images/InfoHST/LOGISTIC.png",
        link: "/ecosystem/rovi-logistics",
        latestNews: [],
    },
    {
        id: "fallback-4",
        index: "04",
        title: "LĨNH VỰC KINH DOANH",
        subTitle: "TÀI CHÍNH",
        description: "Đầu tư chiến lược, M&A, tư vấn và kết nối đối tác nhằm gia tăng giá trị doanh nghiệp.",
        image: "/images/InfoHST/TC.png",
        link: "/ecosystem/rovi-investment",
        latestNews: [],
    },
]

export const InfoEcoSystemSection: React.FC = () => {
    const { data: groups = [], isLoading: groupsLoading, isError } = useGetEcosystemGroupsQuery()
    const [activeIndex, setActiveIndex] = useState(0)
    const router = useRouter()

    // Gọi API lấy tin tức cho từng nhóm
    const newsQueries = useQueries({
        queries: groups.map((group) => ({
            queryKey: ['news', 'by-type', group.id, 4],
            queryFn: async () => {
                const res = await apiGetAllNews({
                    keywords: '',
                    newsTypeId: group.id,
                    isPublic: '',
                    page: 1,
                    pageSize: 4,
                })
                return res?.isOK ? res.result.items : []
            },
            enabled: !!group.id && groups.length > 0,
            staleTime: 5 * 60 * 1000,
        })),
    })

    // --- Helper tạo Slug chuẩn ---
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .replace(/\(rvh\)/g, '')
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    // Xử lý dữ liệu hiển thị
    const categories = groups.length > 0 && !isError
        ? groups.map((group, idx) => {
            const rawNews = newsQueries[idx]?.data ?? [];

            // Sắp xếp tin tức
            const sortedNews = [...rawNews].sort((a, b) => {
                if (a.sort === 0 && b.sort !== 0) return 1;
                if (a.sort !== 0 && b.sort === 0) return -1;
                return a.sort - b.sort;
            });

            const featuredNews = sortedNews[0];

            const apiLink = featuredNews?.url ?? "";

            const hasApiUrl = apiLink.trim() !== "";

            // Logic mới: Tạo slug và ghép vào link
            const slug = createSlug(group.title);
            const targetLink = hasApiUrl ? apiLink : `/ecosystem/${slug}`;

            return {
                id: group.id.toString(),
                index: String(idx + 1).padStart(2, "0"),
                title: "LĨNH VỰC KINH DOANH",
                subTitle: featuredNews?.title || group.title.replace(" (RVH)", ""),
                description: featuredNews?.description || group.description || "Đang cập nhật nội dung...",
                image: featuredNews?.image,
                link: targetLink,
                isExternal: hasApiUrl,
                latestNews: sortedNews,
            };
        })
        : FALLBACK_CATEGORIES;

    useEffect(() => {
        categories.forEach((cat) => {
            if (cat.image && !cat.image.startsWith("data:")) {
                const link = document.createElement("link")
                link.rel = "preload"
                link.as = "image"
                link.href = cat.image
                link.fetchPriority = "high"
                document.head.appendChild(link)
            }
        })
    }, [categories])

    // --- 3. XỬ LÝ CLICK: MỞ TAB MỚI NẾU LÀ API URL ---
    const handleCardClick = (link: string, idx: number, isExternal?: boolean) => {
        setActiveIndex(idx)
        if (isExternal) {
            window.open(link, '_blank')
        } else {
            router.push(link)
        }
    }

    return (
        <section className={cn(
            "relative w-full overflow-hidden font-sans",
            "bg-white h-auto",
            "xl:bg-black xl:h-[100dvh]"
        )}>
            {/* Loading Overlay */}
            {groupsLoading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80">
                    <div className="text-white text-xl animate-pulse">Đang tải...</div>
                </div>
            )}

            {/* Background Images (Desktop Only) */}
            <div className="hidden xl:block absolute inset-0">
                <AnimatePresence mode="popLayout">
                    {categories.map((category, idx) => (
                        activeIndex === idx && (
                            <motion.div
                                key={category.id}
                                className="absolute inset-0"
                                initial={{ opacity: 0, scale: 1.05 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                            >
                                <img
                                    src={category.image}
                                    alt={category.subTitle}
                                    className="h-full w-full object-cover"
                                    loading="eager"
                                    fetchPriority="high"
                                />
                                <div className="absolute inset-0 bg-black/40 xl:bg-black/20 pointer-events-none" />
                            </motion.div>
                        )
                    ))}
                </AnimatePresence>
            </div>

            {/* Content Grid */}
            <div className={cn(
                "relative z-10 w-full h-full",
                "flex flex-col",
                "xl:grid xl:grid-cols-4 xl:grid-rows-1"
            )}>
                {categories.map((category, idx) => (
                    <div
                        key={category.id}
                        // Gọi hàm xử lý click, truyền vào trạng thái isExternal
                        onClick={() => handleCardClick(category.link, idx, (category as any).isExternal)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={cn(
                            "relative flex flex-col justify-end transition-all duration-800 ease-out cursor-pointer group/item",
                            "border-b border-gray-100",
                            "xl:border-white/10 xl:border-b-0",
                            "p-6 pb-8",
                            "xl:p-8 xl:pb-16",
                            "2xl:p-12 2xl:pb-24",
                            "min-[1920px]:p-20 min-[1920px]:pb-40",
                            idx !== categories.length - 1 && "xl:border-r",
                            activeIndex === idx ? "xl:backdrop-blur-[4px] xl:bg-white/5" : "xl:backdrop-blur-none xl:bg-transparent",
                        )}
                    >
                        <div className="relative transition-all duration-800 ease-out h-full flex flex-col justify-end">

                            {/* Index Number */}
                            <h2
                                className={cn(
                                    jakarta.className,
                                    "relative z-0 font-bold select-none leading-[0.75]",
                                    "text-[80px] text-gray-100 absolute -top-6 left-0",
                                    "xl:static xl:text-transparent xl:text-9xl xl:bg-clip-text xl:bg-gradient-to-b xl:from-white xl:to-transparent",
                                    "2xl:text-[11rem]",
                                    "min-[1920px]:text-[16rem]",
                                    "xl:-mb-6 2xl:-mb-10 min-[1920px]:-mb-16",
                                    activeIndex === idx ? "xl:opacity-100 xl:-translate-y-3" : "xl:opacity-20",
                                )}
                            >
                                {category.index}
                            </h2>

                            {/* Category Label */}
                            <div
                                className={cn(
                                    "relative z-10 mt-0 transition-all duration-800 ease-out",
                                    "opacity-100 translate-y-0",
                                    activeIndex === idx ? "xl:opacity-100 xl:translate-y-0" : "xl:opacity-70 xl:translate-y-4",
                                )}
                            >
                                <p className={cn(
                                    jakarta.className,
                                    "font-semibold tracking-[0.2em] uppercase mb-1 pl-1 truncate",
                                    "text-gray-500 text-[10px]",
                                    "xl:text-white/80 xl:text-xs xl:mb-2",
                                    "2xl:text-sm 2xl:mb-3",
                                    "min-[1920px]:text-lg min-[1920px]:mb-4",
                                )}>
                                    <span className="xl:hidden">{category.title}</span>
                                    <span className="hidden xl:inline">{activeIndex === idx ? category.title : ""}</span>
                                </p>

                                <div className="flex items-center xl:items-stretch mb-2 xl:mb-4 min-[1920px]:mb-8 mt-12 xl:mt-0">
                                    <div className={cn(
                                        "hidden xl:block w-[1px] bg-green-600 transition-all duration-500 ease-out",
                                        "-ml-4 mr-4 xl:-ml-8 xl:mr-8 min-[1920px]:-ml-12 min-[1920px]:mr-12",
                                        activeIndex === idx ? "opacity-100 scale-y-100 origin-top" : "opacity-0 scale-y-0 origin-top"
                                    )} />
                                    <div className="xl:hidden w-[3px] h-6 bg-[#006633] mr-3"></div>

                                    {/* Main Title */}
                                    <h3
                                        className={cn(
                                            jakarta.className,
                                            "font-bold tracking-wider transition-all duration-800 ease-out py-1 line-clamp-2",
                                            "text-[#333] text-xl",
                                            "xl:text-white",
                                            activeIndex === idx
                                                ? "xl:text-3xl 2xl:text-4xl min-[1920px]:text-6xl"
                                                : "xl:text-lg 2xl:text-xl min-[1920px]:text-3xl xl:uppercase",
                                        )}
                                    >
                                        {category.subTitle}
                                    </h3>
                                </div>

                                {/* Description (Collapsible on Desktop) */}
                                <div className={cn(
                                    "overflow-hidden transition-all duration-500",
                                    "max-h-full opacity-100",
                                    activeIndex === idx ? "xl:max-h-40 xl:opacity-100" : "xl:max-h-0 xl:opacity-0"
                                )}>
                                    <p
                                        style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}
                                        className={cn(
                                            "leading-relaxed pl-0 line-clamp-4",
                                            "text-gray-600 text-sm",
                                            "xl:text-white/80 xl:text-sm",
                                            "2xl:text-sm 2xl:max-w-md",
                                            "min-[1920px]:text-xl min-[1920px]:max-w-2xl"
                                        )}
                                    >
                                        {category.description}
                                    </p>
                                </div>

                                {/* Mobile Image */}
                                <div className="xl:hidden w-full h-48 rounded-lg overflow-hidden mt-4 shadow-sm">
                                    <img
                                        src={category.image}
                                        alt={category.subTitle}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Arrow Button */}
                        <div
                            className={cn(
                                "absolute bottom-0 right-0 flex items-center justify-center transition-all duration-300 z-30 group/btn bg-[#006633] hover:bg-yellow-500",
                                "hidden xl:flex",
                                "xl:w-16 xl:h-16",
                                "2xl:w-20 2xl:h-20",
                                "min-[1920px]:w-32 min-[1920px]:h-32",
                                activeIndex === idx ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none",
                            )}
                        >
                            <ArrowUpRight
                                strokeWidth={1.5}
                                className={cn(
                                    "text-white transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1",
                                    "xl:w-8 xl:h-8",
                                    "2xl:w-10 2xl:h-10",
                                    "min-[1920px]:w-16 min-[1920px]:h-16"
                                )}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Bottom Divider Line */}
            <div className="hidden xl:flex absolute bottom-0 left-0 w-full">
                {categories.map((_, idx) => (
                    <div key={idx} className="h-0.5 xl:h-1 flex-1 bg-white/10 relative overflow-hidden" />
                ))}
            </div>
        </section>
    )
}

export default InfoEcoSystemSection